/**
 * Contact page "Subscription detail": live, read-only Zuora Product & Charges and Change History for ONE
 * contact_subscriptions row, via the staff-only edge function zuora-subscription-detail (migration 1670).
 */
import { supabase } from "./supabase";

export interface SubscriptionDetailCharge {
  product: string | null;
  ratePlan: string | null;
  ratePlanChange: string | null;
  charge: string | null;
  chargeNumber: string | null;
  description: string | null;
  type: string | null;
  model: string | null;
  isDiscount: boolean;
  isOneTime: boolean;
  price: number | null;
  discountPercentage: number | null;
  discountAmount: number | null;
  pricingSummary: string | null;
  currency: string | null;
  billingPeriod: string | null;
  quantity: number | null;
  uom: string | null;
  total: number | null;
  mrr: number | null;
  tcv: number | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  chargedThroughDate: string | null;
  segment: number | null;
  version: number | null;
}

export interface SubscriptionDetailHistoryEntry {
  kind: "order" | "amendment" | "created";
  version: number | null;
  ref: string | null;
  types: string[];
  details: string[];
  status: string | null;
  bookingDate: string | null;
  contractEffectiveDate: string | null;
  serviceActivationDate: string | null;
  customerAcceptanceDate: string | null;
  description: string | null;
}

export interface SubscriptionDetailDto {
  contract: "zsd/1";
  subscription: {
    id: string | null;
    number: string | null;
    accountId: string | null;
    version: number | null;
    status: string | null;
    termType: string | null;
    autoRenew: boolean | null;
    currentTerm: number | null;
    currentTermPeriodType: string | null;
    renewalTerm: number | null;
    renewalTermPeriodType: string | null;
    subscriptionStartDate: string | null;
    subscriptionEndDate: string | null;
    termStartDate: string | null;
    termEndDate: string | null;
    contractEffectiveDate: string | null;
    serviceActivationDate: string | null;
    customerAcceptanceDate: string | null;
    cancelledDate: string | null;
    contractedMrr: number | null;
    totalContractedValue: number | null;
    currency: string | null;
  };
  charges: SubscriptionDetailCharge[];
  chargesTruncated: boolean;
  history: {
    source: "orders" | "amendments" | "none";
    status: "complete" | "partial" | "unavailable";
    reason: string | null;
    entries: SubscriptionDetailHistoryEntry[];
  };
}

export interface SubscriptionDetailSnapshot {
  fetchedAt: string;
  detail: SubscriptionDetailDto;
  zuoraUrl: string | null;
}

export type SubscriptionDetailResult =
  | ({ status: "ok"; source: "zuora" | "cache" } & SubscriptionDetailSnapshot)
  | {
      status: "unavailable";
      reason: string;
      stale: SubscriptionDetailSnapshot | null;
    }
  | {
      status: "not_in_zuora" | "disabled" | "not_found_in_zuora" | "forbidden";
    };

export class SubscriptionDetailError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "SubscriptionDetailError";
    this.status = status;
  }
}

export async function getSubscriptionDetail(
  contactSubscriptionId: string,
  refresh = false,
): Promise<SubscriptionDetailResult> {
  const res = await supabase.functions.invoke("zuora-subscription-detail", {
    body: { contactSubscriptionId, refresh },
  });
  if (res.error) {
    let status = 500;
    const ctx = (res.error as { context?: Response }).context;
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
    }
    // the server refused this role: render nothing rather than an error
    if (status === 403) {
      return { status: "forbidden" };
    }
    throw new SubscriptionDetailError(
      status === 401
        ? "Your session has expired. Sign in again."
        : "Subscription detail could not be loaded.",
      status,
    );
  }
  return res.data as SubscriptionDetailResult;
}
